import React, { useEffect, useRef, useState } from "react";
import { AggregatedOrderProduct, AggregatedOrderProductCombineStatus } from "./type";
import { useGlobalStore } from "@/store/hooks";
import { getBigPicture } from "@/utils/image";
import classnames from "classnames";
import { Button, Form, InputNumber, Popover, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import ProxyImage from "@/components/ProxyImage";

interface ProductFilterTrProps {
    aggregatedProduct: AggregatedOrderProduct;
    onActiveChange: (id: string, checked: boolean) => void;
    showShopName: boolean;
    showSkuProperty: boolean;
    showSkuExternalId: boolean;
    showTitle: boolean;
    showDetail?: boolean;
    onShowDetailChange?: (show: boolean) => void;
    onPartialSelect: (productId: string, count: number) => Promise<string | null>;
}

interface PartialSelectFormProps {
    max: number;
    onConfirm: (count: number) => Promise<string | null>;
}

interface PartialSelectFormValues {
    count: number;
}

const PartialSelectForm: React.FC<PartialSelectFormProps> = function (props) {
    const { max, onConfirm } = props;
    const [form] = Form.useForm<PartialSelectFormValues>();

    return (
        <Form<PartialSelectFormValues>
            form={form}
            layout="inline"
            onFinish={async ({ count }) => {
                const errorMessage = await onConfirm(count);

                if (errorMessage) {
                    form.setFields([{ name: "count", errors: [errorMessage] }]);
                }
            }}
        >
            <Form.Item
                name="count"
                rules={[
                    {
                        required: true,
                        type: "integer",
                        min: 0,
                        max,
                        message: `请输入 0～${max} 之间的整数`,
                    },
                ]}
            >
                <InputNumber
                    size="small"
                    min={0}
                    max={max}
                    precision={0}
                    style={{ width: 180 }}
                    placeholder="请输入需要选择的数量"
                    autoFocus
                />
            </Form.Item>
            <Form.Item>
                <Button size="small" type="primary" htmlType="submit">
                    确定
                </Button>
            </Form.Item>
        </Form>
    );
};

const ProductFilterTr: React.FC<ProductFilterTrProps> = function (props) {
    const {
        aggregatedProduct,
        onActiveChange,
        showShopName,
        showSkuProperty,
        showSkuExternalId,
        showTitle,
        showDetail,
        onShowDetailChange,
        onPartialSelect,
    } = props;

    const { table } = useOrderManagementStore();
    const [partialSelectOpen, setPartialSelectOpen] = useState(false);
    const [selectAllTipOpen, setSelectAllTipOpen] = useState(false);
    const selectAllTipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (selectAllTipTimer.current !== null) {
                clearTimeout(selectAllTipTimer.current);
            }
        };
    }, []);

    const { isFullSelected, isPartSelected, selectedCount } =
        table.productFilterStore.selectStateMapping.get(aggregatedProduct.id) || {};
    const lockedOrderCount = aggregatedProduct.orderIds.filter((orderId) =>
        table.orderLockStore.lockOrderIdMapping.get(orderId),
    ).length;
    const selectAllTip = lockedOrderCount
        ? lockedOrderCount === aggregatedProduct.orderIds.length
            ? "全部订单不可选（如锁单）"
            : "部分订单不可选（如锁单）"
        : null;

    function handleActiveChange(checked: boolean) {
        if (selectAllTipTimer.current !== null) {
            clearTimeout(selectAllTipTimer.current);
            selectAllTipTimer.current = null;
        }

        const showTip = checked && !!selectAllTip;
        setSelectAllTipOpen(showTip);

        if (showTip) {
            selectAllTipTimer.current = setTimeout(() => {
                setSelectAllTipOpen(false);
                selectAllTipTimer.current = null;
            }, 5000);
        }

        onActiveChange(aggregatedProduct.id, checked);
    }

    const { shopMapping } = useGlobalStore();
    const shop = shopMapping.get(aggregatedProduct.shopId);
    const picture = aggregatedProduct.picture;
    const bigPictureUrl = getBigPicture(picture, aggregatedProduct.shopId);
    const hasTitle = aggregatedProduct.simpleTitle || aggregatedProduct.title;
    const productInfoItems: string[] = [];

    if (showSkuProperty && aggregatedProduct.skuProperties) {
        productInfoItems.push(aggregatedProduct.skuProperties);
    }
    if (showSkuExternalId && aggregatedProduct.sortInfo?.skuExternalId) {
        productInfoItems.push(aggregatedProduct.sortInfo.skuExternalId);
    }
    if (showTitle && hasTitle) {
        productInfoItems.push(hasTitle);
    }

    /** 勾选的展示项没有内容时，降级显示商品标题，避免商品信息区域为空 */
    if (!productInfoItems.length && hasTitle) {
        productInfoItems.push(hasTitle);
    }

    const isChild = aggregatedProduct.combineStatus === AggregatedOrderProductCombineStatus.CHILD;
    const isParent = aggregatedProduct.combineStatus === AggregatedOrderProductCombineStatus.PARENT;

    const tr = (
        <tr
            className={classnames(
                "p10 simple-product-filter-row",
                isChild && "child",
                isPartSelected && !isFullSelected && "part-selected",
                isFullSelected && "full-selected",
                partialSelectOpen && "partial-select-open",
            )}
            onClick={() => {
                const checked = isFullSelected || isPartSelected;

                handleActiveChange(!checked);
            }}
        >
            {showShopName ? <td className="mr5 w50 fs12"> {isParent ? "" : shop?.name}</td> : null}
            <td className="mr5">
                <div className="flex flex-center">
                    <span>
                        <Tooltip title={<ProxyImage src={bigPictureUrl} alt="" width="200" height="auto" />}>
                            <ProxyImage src={picture} alt="" width="40" height="40" />
                        </Tooltip>
                    </span>

                    <span className={classnames("fs12 ml5 f1 word-break-all")}>
                        {productInfoItems.map((item, index) => (
                            <React.Fragment key={`${index}-${item}`}>
                                {index > 0 ? <br /> : null}
                                <span className={classnames(index > 0 && "text-gray small")}>{item}</span>
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            </td>
            <td className="product-filter-quantity">
                <div>
                    <span>
                        <span className="error">{selectedCount || 0}</span>
                        <span>/</span>
                        <span>{aggregatedProduct.quantity}</span>
                    </span>
                    {isParent ? (
                        <Button
                            type="link"
                            size="small"
                            className="ml3 small product-filter-detail-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShowDetailChange(!showDetail);
                            }}
                        >
                            {showDetail ? "收起" : "详情"}
                        </Button>
                    ) : null}
                </div>
                <div
                    className="product-filter-quantity-actions"
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <button
                        type="button"
                        className="product-filter-select-all"
                        onClick={() => {
                            handleActiveChange(true);
                        }}
                    >
                        全选
                    </button>
                    <Popover
                        trigger="click"
                        open={partialSelectOpen}
                        onOpenChange={setPartialSelectOpen}
                        placement="top"
                        zIndex={900}
                        destroyOnHidden
                        content={
                            <PartialSelectForm
                                max={aggregatedProduct.quantity}
                                onConfirm={async (count) => {
                                    const errorMessage = await onPartialSelect(aggregatedProduct.id, count);

                                    if (errorMessage) return errorMessage;

                                    setPartialSelectOpen(false);
                                    return null;
                                }}
                            />
                        }
                    >
                        <button type="button" className="product-filter-partial-btn">
                            部分选
                        </button>
                    </Popover>
                </div>
            </td>
        </tr>
    );

    return (
        <Tooltip title={selectAllTip} open={selectAllTipOpen && !!selectAllTip} trigger={[]} placement="top" zIndex={900}>
            {tr}
        </Tooltip>
    );
};

export default observer(ProductFilterTr);
